import React, { useEffect, useState } from "react";
import { ArrowLeft, Plus, Trash2, GripVertical } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { Card, RedButton, GhostButton, Field, inputCls } from "../../components/crm/ui";
import { toast } from "sonner";

export default function FormBuilder({ onDone, onCancel }) {
  const [templates, setTemplates] = useState([]);
  const [library, setLibrary] = useState({});
  const [sources, setSources] = useState([]);
  const [meta, setMeta] = useState({ name: "", template: "lead_capture", source: "CRM Form", stage: "", follow_up_rule: true, confirmation_message: "Thank you! We'll be in touch soon." });
  const [fields, setFields] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/forms/templates").then(({ data }) => {
      setTemplates(data.templates); setLibrary(data.field_library);
      applyTemplate("lead_capture", data.templates, data.field_library);
    });
    api.get("/sources").then(({ data }) => setSources(data.items));
  }, []);

  const applyTemplate = (key, tmpls = templates, lib = library) => {
    const t = tmpls.find((x) => x.key === key);
    if (!t) return;
    setFields(t.fields.map((k) => ({ key: k, ...(lib[k] || { label: k, type: "text", required: false }) })));
    setMeta((m) => ({ ...m, template: key, name: m.name || t.name }));
  };

  const addField = (key) => { if (fields.find((f) => f.key === key)) return; setFields([...fields, { key, ...library[key] }]); };
  const removeField = (key) => setFields(fields.filter((f) => f.key !== key));
  const toggleReq = (key) => setFields(fields.map((f) => f.key === key ? { ...f, required: !f.required } : f));

  const save = async () => {
    if (!meta.name) { toast.error("Give the form a name"); return; }
    setSaving(true);
    try { await api.post("/forms", { ...meta, fields }); toast.success("Form created"); onDone(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <button onClick={onCancel} className="text-sm text-slate-500 hover:text-slate-900 flex items-center gap-1.5 mb-4"><ArrowLeft size={15} /> Forms</button>
      <h1 className="font-display text-2xl font-bold text-slate-900 mb-6">Create Form</h1>
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card className="p-6 space-y-4">
            <Field label="Form name"><input className={inputCls} value={meta.name} onChange={(e) => setMeta({ ...meta, name: e.target.value })} data-testid="fb-name" placeholder="e.g. Plan Your Visit" /></Field>
            <Field label="Start from template">
              <select className={inputCls} value={meta.template} onChange={(e) => applyTemplate(e.target.value)} data-testid="fb-template">
                {templates.map((t) => <option key={t.key} value={t.key}>{t.name}</option>)}
              </select>
            </Field>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between mb-4"><h3 className="font-display font-bold text-slate-900">Fields</h3></div>
            <div className="space-y-2">
              {fields.map((f) => (
                <div key={f.key} className="flex items-center gap-3 bg-slate-50 rounded-md px-3 py-2.5" data-testid={`fb-field-${f.key}`}>
                  <GripVertical size={15} className="text-slate-300" />
                  <span className="flex-1 text-sm font-medium text-slate-700">{f.label} <span className="text-xs text-slate-400">({f.type})</span></span>
                  <label className="flex items-center gap-1.5 text-xs text-slate-500"><input type="checkbox" checked={!!f.required} onChange={() => toggleReq(f.key)} className="accent-red-600" /> Required</label>
                  <button onClick={() => removeField(f.key)} className="text-slate-400 hover:text-alto-red"><Trash2 size={15} /></button>
                </div>
              ))}
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {Object.keys(library).filter((k) => !fields.find((f) => f.key === k)).map((k) => (
                <button key={k} onClick={() => addField(k)} data-testid={`fb-add-${k}`} className="text-xs font-medium px-3 py-1.5 rounded-full border border-slate-200 hover:border-alto-red hover:text-alto-red text-slate-600 inline-flex items-center gap-1"><Plus size={12} /> {library[k].label}</button>
              ))}
            </div>
          </Card>
        </div>

        <div className="space-y-6">
          <Card className="p-6 space-y-4">
            <h3 className="font-display font-bold text-slate-900">Automation</h3>
            <Field label="Source"><select className={inputCls} value={meta.source} onChange={(e) => setMeta({ ...meta, source: e.target.value })} data-testid="fb-source">{sources.map((s) => <option key={s.id} value={s.name}>{s.name}</option>)}</select></Field>
            <label className="flex items-center gap-2.5 text-sm text-slate-700"><input type="checkbox" checked={meta.follow_up_rule} onChange={(e) => setMeta({ ...meta, follow_up_rule: e.target.checked })} className="accent-red-600" data-testid="fb-followup" /> Auto-create a follow-up task on submission</label>
            <Field label="Confirmation message"><textarea className={inputCls + " resize-none"} rows={3} value={meta.confirmation_message} onChange={(e) => setMeta({ ...meta, confirmation_message: e.target.value })} data-testid="fb-confirm" /></Field>
          </Card>
          <div className="flex gap-3">
            <GhostButton className="flex-1" onClick={onCancel}>Cancel</GhostButton>
            <RedButton className="flex-1" onClick={save} disabled={saving} data-testid="fb-save">{saving ? "Saving…" : "Create Form"}</RedButton>
          </div>
        </div>
      </div>
    </div>
  );
}
